import { useMemo } from 'react';

/**
 * Helper hook for React Router integration with AccessibilityController
 * 
 * @example
 * // In a React Router app:
 * import { useParams, useNavigate } from 'react-router-dom';
 * 
 * function AccessibilityPage() {
 *   const { tool } = useParams<{ tool?: string }>();
 *   const navigate = useNavigate();
 *   
 *   const router = useAccessibilityRouter({
 *     currentRoute: tool,
 *     onRouteChange: (route) => navigate(`/settings/accessibility/${route}`),
 *     basePath: '/settings/accessibility'
 *   });
 * 
 *   return <AccessibilityController router={router} />;
 * }
 */
export interface AccessibilityRouterConfig {
  /** Current route parameter (e.g., 'visual-accessibility') */
  currentRoute?: string;
  /** Callback when route changes */
  onRouteChange: (route: string) => void;
  /** Base path for routes (e.g., '/settings/accessibility') */
  basePath?: string;
}

/**
 * Creates a router config object for AccessibilityController
 */
export function useAccessibilityRouter(config: AccessibilityRouterConfig) {
  return useMemo(() => ({
    currentRoute: config.currentRoute,
    onRouteChange: config.onRouteChange,
    basePath: config.basePath || '/accessibility',
  }), [config.currentRoute, config.onRouteChange, config.basePath]);
}

/**
 * Valid accessibility tool routes
 */
export const ACCESSIBILITY_ROUTES = [
  'text-size', 
  'visual-accessibility',
  'layout-interaction',
  'motion-animation',
  'focus-navigation',
  'reading-comprehension',
  'color-visibility',
  'audio-alerts'
] as const;

/**
 * Type for accessibility route parameters
 */
export type AccessibilityRoute = typeof ACCESSIBILITY_ROUTES[number];

/**
 * Helper function to validate if a route is a valid accessibility tool
 */
export function isValidAccessibilityRoute(route: string): route is AccessibilityRoute {
  return ACCESSIBILITY_ROUTES.includes(route as AccessibilityRoute);
}

/**
 * Helper function to get the default route
 */
export function getDefaultAccessibilityRoute(): AccessibilityRoute {
  return 'text-size';
}
